import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBooking } from "@/context/BookingContext";
import { isBookingEnabled, getBookingLink } from "@/config/booking";
import { trackEvent } from "@/utils/analytics";
import "./BookingModal.scss";

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const BookingModal: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, location, closeBooking } = useBooking();
  const [embedLoaded, setEmbedLoaded] = useState(false);
  const [CalComponent, setCalComponent] = useState<React.ComponentType<{
    calLink: string;
    style?: React.CSSProperties;
    config?: Record<string, string>;
    onBookingSuccessful?: () => void;
  }> | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = "booking-modal-title";

  // Capture the trigger element when the modal opens so focus can be restored
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
      setEmbedLoaded(false);
    } else {
      // Restore focus to the trigger element when closing
      if (triggerRef.current) {
        triggerRef.current.focus();
        triggerRef.current = null;
      }
    }
  }, [isOpen]);

  // Body-scroll lock
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Lazy-load Cal embed only when the modal opens and booking is enabled
  useEffect(() => {
    if (!isOpen || !isBookingEnabled()) return;
    let cancelled = false;
    import("@calcom/embed-react").then((mod) => {
      if (!cancelled) {
        const Cal = mod.default as React.ComponentType<{
          calLink: string;
          style?: React.CSSProperties;
          config?: Record<string, string>;
          onBookingSuccessful?: () => void;
        }>;
        setCalComponent(() => Cal);
        setEmbedLoaded(true);
        trackEvent("booking_started", { location });
      }
    }).catch(() => {
      // embed failed to load; user will see the fallback content
    });
    return () => { cancelled = true; };
  }, [isOpen, location]);

  // Focus trap — keep focus within the modal while open
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      closeBooking();
      return;
    }
    if (e.key !== "Tab") return;
    const focusable = modalRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [closeBooking]);

  // Focus the close button when modal first opens
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => closeBtnRef.current?.focus());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const enabled = isBookingEnabled();

  const handleBookingSuccessful = () => {
    trackEvent("booking_confirmed", { location });
  };

  return (
    <div
      className="booking-overlay"
      onClick={closeBooking}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="booking-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        aria-hidden={false}
      >
        <div className="booking-modal__header">
          <div>
            <h2 id={titleId} className="booking-modal__title">
              {t("booking.modalTitle")}
            </h2>
            <p className="booking-modal__subtitle">{t("booking.subtitle")}</p>
          </div>
          <button
            ref={closeBtnRef}
            className="booking-modal__close"
            onClick={closeBooking}
            aria-label={t("booking.close")}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="booking-modal__body">
          {enabled ? (
            <>
              {!embedLoaded && (
                <div className="booking-modal__loading" aria-live="polite">
                  {t("booking.loading")}
                </div>
              )}
              {CalComponent && (
                <CalComponent
                  calLink={getBookingLink()}
                  style={{ width: "100%", height: "100%", overflow: "scroll" }}
                  config={{ theme: "auto" }}
                  onBookingSuccessful={handleBookingSuccessful}
                />
              )}
            </>
          ) : (
            <p className="booking-modal__fallback">{t("booking.fallbackNote")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
